// Lines of a block tokenizer's source, split lazily.
//
// marked calls every extension's `tokenize` with the whole rest of the document
// at every block boundary. Splitting that with `src.split("\n")` each time makes
// one parse cost "blocks x document length" (hundreds of milliseconds on a
// 150,000-character page). Splitting only the lines that are read keeps the cost
// proportional to what is read.

/** The same lines as `src.split("\n")`, split only as far as `at(i)` needs. */
export class Lines {
  private readonly lines: string[] = [];
  /** Where each line ends (before its line break). */
  private readonly ends: number[] = [];
  private pos = 0;
  private done = false;

  constructor(private readonly src: string) {}

  /** Line i, or undefined past the end (like indexing the split array). */
  at(i: number): string | undefined {
    while (!this.done && this.lines.length <= i) {
      const nl = this.src.indexOf("\n", this.pos);
      const end = nl === -1 ? this.src.length : nl;
      this.lines.push(this.src.slice(this.pos, end));
      this.ends.push(end);
      if (nl === -1) this.done = true;
      else this.pos = nl + 1;
    }
    return this.lines[i];
  }

  /** Lines `from` to `to` (exclusive) joined with "\n" (`slice(from, to).join("\n")`). */
  join(from: number, to: number): string {
    if (to <= from || this.at(to - 1) === undefined) return "";
    const start = from === 0 ? 0 : this.ends[from - 1] + 1;
    return this.src.slice(start, this.ends[to - 1]);
  }
}

/** The first line (without its line break). */
export function firstLine(src: string): string {
  const nl = src.indexOf("\n");
  return nl === -1 ? src : src.slice(0, nl);
}
